import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import authService from '@/services/authService'
import router from '@/router'
import { 
  hasRouteAccess, 
  isAdminRole, 
  getDefaultRoute,
  getAllowedSidebarItems 
} from '@/config/rbac'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value)
  const currentUser = computed(() => user.value)
  
  const userRole = computed(() => {
    // Handle role as object or string
    const role = user.value?.role || user.value?.role_name || null
    if (typeof role === 'object' && role !== null) {
      return role.name || role.display_name || null
    }
    return role
  })
  
  // RBAC computed properties
  const isAdmin = computed(() => isAdminRole(userRole.value))
  
  const isChecker = computed(() => {
    const role = userRole.value
    if (!role) return false
    return role.toLowerCase() === 'checker'
  })

  const isLeadChecker = computed(() => {
    const role = userRole.value
    if (!role) return false
    return role.toLowerCase() === 'lead-checker'
  })
  
  const isSupervisor = computed(() => {
    const role = userRole.value
    if (!role) return false
    return role.toLowerCase() === 'spv'
  })
  
  // Get allowed sidebar items for current user
  const allowedSidebarItems = computed(() => {
    return getAllowedSidebarItems(userRole.value)
  })
  
  // Get default route for current user
  const defaultRoute = computed(() => {
    return getDefaultRoute(userRole.value)
  })

  // Methods
  /**
   * Check if current user can access a specific route
   * @param {string} routeName - The route name
   * @returns {boolean}
   */
  const canAccessRoute = (routeName) => {
    return hasRouteAccess(userRole.value, routeName)
  }
  
  /**
   * Check if current user can see a sidebar menu item
   * @param {string} menuName - The menu item name
   * @returns {boolean}
   */
  const canSeeSidebarItem = (menuName) => {
    const allowed = allowedSidebarItems.value
    // null means full access
    if (allowed === null) return true
    return allowed.includes(menuName)
  }

  // Actions
  const initAuth = () => {
    // Load auth data from localStorage on app start
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('auth_user')

    if (savedToken && savedUser) {
      token.value = savedToken
      try {
        user.value = JSON.parse(savedUser)
      } catch (e) {
        console.error('Failed to parse saved user data:', e)
        logout()
      }
    }
  }

  const login = async (credentials) => {
    try {
      loading.value = true
      error.value = null

      const response = await authService.login(credentials)

      // CUSTOMIZE: Adjust based on your API response structure
      // Common patterns:
      // 1. { success: true, data: { token, user } }
      // 2. { token, user }
      // 3. { access_token, user }

      const authToken = response.data?.token || response.token || response.access_token
      const userData = response.data?.user || response.user

      if (!authToken) {
        throw new Error('No token received from server')
      }

      // Save to state
      token.value = authToken
      user.value = userData

      // Save to localStorage
      localStorage.setItem('auth_token', authToken)
      localStorage.setItem('auth_user', JSON.stringify(userData))

      return { success: true, user: userData }
    } catch (err) {
      error.value = err.message || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  const register = async (userData) => {
    try {
      loading.value = true
      error.value = null

      const response = await authService.register(userData)

      // CUSTOMIZE: Some APIs auto-login after registration
      const authToken = response.data?.token || response.token
      const user = response.data?.user || response.user

      if (authToken && user) {
        token.value = authToken
        user.value = user
        localStorage.setItem('auth_token', authToken)
        localStorage.setItem('auth_user', JSON.stringify(user))
      }

      return { success: true, data: response }
    } catch (err) {
      error.value = err.message || 'Registration failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      loading.value = true

      // Call logout API (optional, some apps skip this)
      try {
        await authService.logout()
      } catch (err) {
        console.error('Logout API call failed:', err)
      }

      // Clear state
      user.value = null
      token.value = null
      error.value = null

      // Clear localStorage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')

      // Redirect to login
      router.push({ name: 'login' })
    } finally {
      loading.value = false
    }
  }

  const fetchCurrentUser = async () => {
    try {
      loading.value = true
      error.value = null

      const response = await authService.getCurrentUser()

      const userData = response.data?.user || response.user || response

      user.value = userData
      localStorage.setItem('auth_user', JSON.stringify(userData))

      return userData
    } catch (err) {
      error.value = err.message || 'Failed to fetch user'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateProfile = async (profileData) => {
    try {
      loading.value = true
      error.value = null

      const response = await authService.updateProfile(profileData)

      const userData = response.data?.user || response.user || response

      user.value = userData
      localStorage.setItem('auth_user', JSON.stringify(userData))

      return userData
    } catch (err) {
      error.value = err.message || 'Failed to update profile'
      throw err
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    try {
      loading.value = true
      error.value = null

      await authService.changePassword(passwordData)

      return { success: true }
    } catch (err) {
      error.value = err.message || 'Failed to change password'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    token,
    loading,
    error,

    // Getters
    isAuthenticated,
    currentUser,
    userRole,
    isAdmin,
    isChecker,
    isLeadChecker,
    isSupervisor,
    allowedSidebarItems,
    defaultRoute,

    // RBAC Methods
    canAccessRoute,
    canSeeSidebarItem,

    // Actions
    initAuth,
    login,
    register,
    logout,
    fetchCurrentUser,
    updateProfile,
    changePassword,
  }
})
